import { useEffect, useState } from "react";
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../api";
import "./Users.css";

const ROLES = ["loan_officer", "manager", "admin", "executive"];

function UserModal({ mode, user, managers, onClose, onSave }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState({
    full_name:   user?.full_name   || "",
    email:       user?.email       || "",
    role:        user?.role        || "loan_officer",
    nmls_number: user?.nmls_number || "",
    manager_id:  user?.manager_id  || "",
    is_active:   user?.is_active   !== undefined ? user.is_active : true,
    password:    "",
    confirm:     "",
  });
  const [error, setError]       = useState("");
  const [saving, setSaving]     = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");
    if (!isEdit && form.password !== form.confirm) { setError("Passwords do not match"); return; }
    if (!isEdit && form.password.length < 8)        { setError("Password must be at least 8 characters"); return; }
    if (isEdit && form.password && form.password !== form.confirm) { setError("Passwords do not match"); return; }

    if (form.role === "loan_officer" && !form.nmls_number.trim()) {
      setError("NMLS number is required for loan officers");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        full_name:   form.full_name,
        role:        form.role,
        nmls_number: form.nmls_number.trim() || null,
        manager_id:  form.manager_id ? parseInt(form.manager_id) : null,
        is_active:   form.is_active,
      };
      if (!isEdit) {
        payload.email    = form.email;
        payload.password = form.password;
      } else if (form.password) {
        payload.password = form.password;
      }

      if (isEdit) {
        await api.put(`/users/${user.id}`, payload);
      } else {
        await api.post("/users/", payload);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-card">
        <div className="modal-header">
          <h2>{isEdit ? `Edit ${user?.full_name}` : "Create User"}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && <div className="modal-error">{error}</div>}

          <div className="form-row-2">
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                value={form.full_name}
                onChange={e => setForm({...form, full_name: e.target.value})}
                required placeholder="Jane Smith"
              />
            </div>

            {!isEdit && (
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm({...form, email: e.target.value})}
                  required placeholder="jane@rosegate.com"
                />
              </div>
            )}

            {isEdit && (
              <div className="form-group">
                <label>Email Address</label>
                <input type="email" value={form.email || user?.email} disabled style={{opacity:0.6}} />
                <span className="field-hint">Email cannot be changed</span>
              </div>
            )}
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label>Role</label>
              <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
                {ROLES.map(r => (
                  <option key={r} value={r}>{r.replace("_", " ").replace(/(^|\s)\w/g, c => c.toUpperCase())}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Manager <span className="optional-tag">optional</span></label>
              <select value={form.manager_id} onChange={e => setForm({...form, manager_id: e.target.value})}>
                <option value="">— No manager —</option>
                {managers.map(m => (
                  <option key={m.id} value={m.id}>{m.full_name}</option>
                ))}
              </select>
            </div>
          </div>

          {form.role === "loan_officer" && (
            <div className="form-group">
              <label>NMLS Number</label>
              <input
                type="text"
                value={form.nmls_number}
                onChange={e => setForm({...form, nmls_number: e.target.value})}
                required
                placeholder="e.g. 123456"
              />
            </div>
          )}

          {isEdit && (
            <div className="form-group">
              <label>Status</label>
              <div className="status-toggle">
                <button
                  type="button"
                  className={`toggle-btn ${form.is_active ? "active" : ""}`}
                  onClick={() => setForm({...form, is_active: true})}
                >Active</button>
                <button
                  type="button"
                  className={`toggle-btn ${!form.is_active ? "active danger" : ""}`}
                  onClick={() => setForm({...form, is_active: false})}
                >Inactive</button>
              </div>
            </div>
          )}

          <div className="password-section">
            <div className="password-section-label">
              {isEdit ? "Change Password" : "Set Password"}
              {isEdit && <span className="optional-tag">leave blank to keep current</span>}
            </div>
            <div className="form-row-2">
              <div className="form-group">
                <label>{isEdit ? "New Password" : "Password"}</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  required={!isEdit}
                  placeholder="••••••••"
                  minLength={isEdit && form.password ? 8 : undefined}
                />
              </div>
              <div className="form-group">
                <label>Confirm Password</label>
                <input
                  type="password"
                  value={form.confirm}
                  onChange={e => setForm({...form, confirm: e.target.value})}
                  required={!isEdit}
                  placeholder="••••••••"
                />
                {form.confirm && form.password !== form.confirm && (
                  <span className="field-hint" style={{color:"var(--danger)"}}>Passwords do not match</span>
                )}
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Users() {
  const { user, impersonateUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [modal, setModal]         = useState(null); // null | { mode: "create" } | { mode: "edit", user }
  const [impersonating, setImpersonating] = useState(null);
  const [managers, setManagers]   = useState([]);

  const load = () => {
    setLoading(true);
    api.get("/users/").then(r => {
      setUsers(r.data);
      setManagers(r.data.filter(u => u.role === "manager" || u.role === "admin"));
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleImpersonate = async (target) => {
    setImpersonating(target.id);
    try {
      await impersonateUser(target);
      navigate("/dashboard");
    } catch (err) {
      alert(err.response?.data?.detail || "Could not impersonate user");
    } finally { setImpersonating(null); }
  };

  const filtered = users.filter(u =>
    u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.role?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="loading">Loading users...</div>;

  return (
    <div className="users-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="page-subtitle">{users.length} total users</p>
        </div>
        {user?.role === "admin" && (
          <button className="btn-secondary btn-sm" onClick={() => setModal({ mode: "create" })}>
            + Create User
          </button>
        )}
      </div>

      {user?.role === "admin" && (
        <div className="impersonate-info-card">
          <span className="info-icon">💡</span>
          <span>Click <strong>Edit</strong> to change a user's role, manager, or password. Click <strong>View As</strong> to impersonate any user.</span>
        </div>
      )}

      <div className="table-card">
        <div className="table-toolbar">
          <input
            className="search-input"
            placeholder="Search by name, email or role..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <span className="result-count">{filtered.length} results</span>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Manager</th>
              <th>Status</th>
              {user?.role === "admin" && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="empty-row">No users found</td></tr>
            )}
            {filtered.map(u => {
              const mgr = managers.find(m => m.id === u.manager_id);
              return (
                <tr key={u.id}>
                  <td>
                    <div className="user-cell">
                      <div className="user-cell-avatar">{u.full_name?.[0]?.toUpperCase()}</div>
                      <div>
                        <div className="user-cell-name">{u.full_name}</div>
                        {u.id === user?.id && <div className="you-tag">You</div>}
                      </div>
                    </div>
                  </td>
                  <td className="mono text-secondary">{u.email}</td>
                  <td><span className={`badge badge-${u.role}`}>{u.role?.replace("_", " ")}</span></td>
                  <td className="text-secondary">{mgr?.full_name || "—"}</td>
                  <td><span className={`badge ${u.is_active ? "badge-active" : "badge-cancelled"}`}>{u.is_active ? "Active" : "Inactive"}</span></td>
                  {user?.role === "admin" && (
                    <td>
                      <div className="action-btns">
                        <button
                          className="btn-edit"
                          onClick={() => setModal({ mode: "edit", user: u })}
                        >
                          ✎ Edit
                        </button>
                        {u.email !== user.email && (
                          <button
                            className="impersonate-btn"
                            onClick={() => handleImpersonate(u)}
                            disabled={impersonating === u.id}
                          >
                            {impersonating === u.id ? "..." : "👁️ View As"}
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal && (
        <UserModal
          mode={modal.mode}
          user={modal.user}
          managers={managers}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
